import {
  Box,
  Flex,
  HStack,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  Text,
} from '@chakra-ui/react';
import { faArrowUp, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useRef, useState } from 'react';
import { Mention, MentionsInput } from 'react-mentions';
import { useQuery } from 'urql';
import { Point } from '../../domain/geometry/geometric-types';
import { ListUsersDocument } from '../../gql/graphql';
import { useUserTenant } from '../../services/auth-info';
import { useViewer } from '../common/ForgeViewer';
import { useSheetViewer } from '../common/SheetViewer';
import { SetCursorTool } from '../common/forgeViewerInteractions/SetCursorTool';
import SparkelIcon from '../common/icon/SparkelIcon';
import { useViewerMode, ViewerMode } from '../common/viewer-mode';
import { PointShapeDrawing } from '../orderContractor/shapes/PointShapeDrawing';
import { PointDrawingResult } from '../orderContractor/shapes/ShapeDrawing';
import { useComments } from './useComments';

interface CommentEditorProps {
  isUpdate?: boolean;
  replyingTo?: string;
  isNewComment?: boolean;
  id?: string;
  parentId?: string;
  closeEditor: () => void;
}

function NewCommentPopover({
  isUpdate,
  replyingTo,
  isNewComment,
  parentId,
  closeEditor,
}: CommentEditorProps) {
  const AuthUser = useUserTenant().username;

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogPosition, setDialogPosition] = useState({ x: 0, y: 0 });

  const { viewer } = useViewer();
  const { viewerRef } = useSheetViewer();

  const { viewerMode } = useViewerMode();
  const [commentText, setCommentText] = useState('');
  const [anchorPoint, setAnchorPoint] = useState<Point>();
  const { reply, addTopLevelComment } = useComments();

  let ref: HTMLElement | null = null;

  if (viewer && viewerRef.current) {
    ref =
      viewerMode === ViewerMode.Models ? viewer?.container : viewerRef.current;
  }

  const handleDocumentClick = (e: MouseEvent) => {
    if ((!isDialogOpen || !anchorPoint) && ref) {
      // Calculate the position relative to the button
      const rect = ref.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Set the position of the dialogue
      setDialogPosition({ x, y });

      // Open the dialogue
      setIsDialogOpen(true);
    }
  };

  useEffect(() => {
    const current = ref;
    if (!current) {
      return;
    }

    const setCursorTool = new SetCursorTool(
      'url(/src/assets/icons/comment-icon.png), crosshair'
    );

    // Attach a click event listener to the document
    current.addEventListener('click', handleDocumentClick);
    if (viewer) {
      viewer.toolController.registerTool(setCursorTool);
      viewer.toolController.activateTool(setCursorTool.getName());
    }

    // Clean up the event listener when the component unmounts
    return () => {
      if (viewer) {
        current.removeEventListener('click', handleDocumentClick);

        viewer.toolController.deregisterTool(setCursorTool);
        viewer.toolController.deactivateTool(setCursorTool.getName());
      }
    };
  }, []);

  const [{ data: listUsersData }] = useQuery({
    query: ListUsersDocument,
  });

  const _handleClick = () => {
    if (commentText === '') return;

    // reply
    if (!isUpdate && !isNewComment) {
      let cleanedText = removeTagFromCommentText().trim();
      reply(parentId, AuthUser, cleanedText, replyingTo);
      closeEditor();
    }

    // new Comment
    if (isNewComment) {
      addTopLevelComment(commentText, AuthUser, undefined, anchorPoint);
      closeEditor();
      if (viewer) {
        viewer.canvas.style.cursor = 'auto';
      }
    }

    reset();
  };

  function reset() {
    setCommentText('');
  }

  // Define a function to extract the plain text without mentions
  const removeTagFromCommentText = () => {
    return commentText.replace(/@\w+\s/g, ''); // This regex removes mentions like "@username "
  };

  const commentInputRef = useRef<HTMLInputElement | null>(null);

  return (
    <>
      {viewer ? (
        <Box hidden={true}>
          <PointShapeDrawing
            viewer={viewer}
            onResult={function (result: PointDrawingResult): void {
              if (result.point) {
                setAnchorPoint(result.point);
              }
            }}
          ></PointShapeDrawing>
        </Box>
      ) : null}
      <Popover
        closeOnBlur={false}
        isOpen={isDialogOpen}
        onClose={closeEditor}
        initialFocusRef={commentInputRef}
        isLazy
      >
        <PopoverContent
          style={{
            position: 'absolute',
            top: `${dialogPosition.y}px`,
            left: `${dialogPosition.x}px`,
          }}
          borderRadius={'2xl'}
          borderTopLeftRadius={'sm'}
          shadow={'xl'}
        >
          <PopoverBody>
            {anchorPoint ? (
              <Flex alignItems={'start'} justifyContent="space-between">
                {/* New Comment Box */}
                <Box>
                  <MentionsInput
                    inputRef={commentInputRef}
                    style={{
                      input: {
                        paddingLeft: '4px',
                        outline: 'none',
                        fontSize: '0.9em',
                        paddingTop: '2px',
                        maxwidth: '100%',
                      },
                    }}
                    value={commentText}
                    autoFocus={true}
                    onChange={(e) => {
                      setCommentText(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        _handleClick();
                      }
                    }}
                    placeholder={`${
                      isNewComment ? 'Add a comment' : 'Write a new reply'
                    }`}
                  >
                    <Mention
                      trigger="@"
                      data={
                        listUsersData?.listUsers
                          ? listUsersData?.listUsers.map((user) => ({
                              id: user.id,
                              display: user.email,
                            }))
                          : []
                      }
                      renderSuggestion={(suggestion) => (
                        <div>{suggestion.display}</div>
                      )}
                    />
                  </MentionsInput>
                </Box>
                <HStack gap={1} height={'full'} alignItems={'flex-end'}>
                  <IconButton
                    icon={
                      <SparkelIcon
                        icon={faTimes}
                        color={['gray.800', 'gray.50']}
                      />
                    }
                    colorScheme="gray"
                    size={'xs'}
                    onClick={closeEditor}
                    aria-label={'close editor'}
                  />
                  <IconButton
                    icon={
                      <SparkelIcon
                        icon={faArrowUp}
                        color={['white', 'gray.800']}
                      />
                    }
                    size={'xs'}
                    onClick={() => _handleClick()}
                    aria-label={'send comment'}
                  />
                </HStack>
              </Flex>
            ) : (
              <Box>
                <Text>Comments are required to be anchored on the model</Text>
              </Box>
            )}
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </>
  );
}

export default NewCommentPopover;
